import axios from 'axios';

// Get all technician inventory records
export const getTechnicianInventory = async (filters = {}) => {
  try {
    let queryString = '';
    if (filters.technician_id) queryString += `technician_id=${filters.technician_id}&`;
    if (filters.status) queryString += `status=${filters.status}&`;
    if (filters.part_id) queryString += `part_id=${filters.part_id}&`;

    const token = localStorage.getItem('token');
    const res = await axios.get(`/api/technician-inventory${queryString ? '?' + queryString : ''}`, {
      headers: {
        'x-auth-token': token
      }
    });
    return res.data;
  } catch (error) {
    throw error.response?.data || { message: 'Error fetching technician inventory' };
  }
};

// Issue inventory to a technician
export const issueInventoryToTechnician = async (issueData) => {
  try {
    const token = localStorage.getItem('token');
    const res = await axios.post('/api/technician-inventory/issue', issueData, {
      headers: {
        'Content-Type': 'application/json',
        'x-auth-token': token
      }
    });
    return res.data;
  } catch (error) {
    throw error.response?.data || { message: 'Error issuing inventory to technician' };
  }
};

// Update technician inventory status
export const updateTechnicianInventoryStatus = async (recordId, statusData) => {
  try {
    const token = localStorage.getItem('token');
    const res = await axios.put(`/api/technician-inventory/${recordId}/status`, statusData, {
      headers: {
        'Content-Type': 'application/json',
        'x-auth-token': token
      }
    });
    return res.data;
  } catch (error) {
    throw error.response?.data || { message: 'Error updating technician inventory status' };
  }
};

// Return inventory from technician
export const returnInventoryFromTechnician = async (recordId, returnData) => {
  try {
    const token = localStorage.getItem('token');
    const res = await axios.post(`/api/technician-inventory/${recordId}/return`, returnData, {
      headers: {
        'Content-Type': 'application/json',
        'x-auth-token': token
      }
    });
    return res.data;
  } catch (error) {
    throw error.response?.data || { message: 'Error returning inventory from technician' };
  }
};

// Get technician inventory summary
export const getTechnicianInventorySummary = async (technicianId) => {
  try {
    const token = localStorage.getItem('token');
    const res = await axios.get(`/api/technician-inventory/technician/${technicianId}/summary`, {
      headers: {
        'x-auth-token': token
      }
    });
    return res.data;
  } catch (error) {
    throw error.response?.data || { message: 'Error fetching technician inventory summary' };
  }
};

// Helper function to get status display text
export const getStatusDisplayText = (status) => {
  const statusMap = {
    'assigned': 'Assigned',
    'on_truck': 'On Truck',
    'used': 'Used',
    'need_to_return': 'Need to Return',
    'returned': 'Returned'
  };
  return statusMap[status] || status;
};

// Helper function to get status color
export const getStatusColor = (status) => {
  const colorMap = {
    'assigned': 'info',
    'on_truck': 'primary',
    'used': 'success',
    'need_to_return': 'warning',
    'returned': 'default'
  };
  return colorMap[status] || 'default';
};

// Helper function to get available status transitions
export const getAvailableStatusTransitions = (currentStatus) => {
  const transitions = {
    'assigned': ['on_truck', 'used', 'need_to_return', 'returned'],
    'on_truck': ['used', 'need_to_return', 'returned'],
    'used': ['need_to_return', 'returned'],
    'need_to_return': ['returned'],
    'returned': [] // No transitions from returned
  };
  return transitions[currentStatus] || [];
};

// Helper function to check if status allows returns
export const canReturnInventory = (status) => {
  return ['assigned', 'on_truck', 'used', 'need_to_return'].includes(status);
};

// Get parts issued to technicians for bundle printing
export const getPartsForBundlePrinting = async (warehouseId) => {
  try {
    const token = localStorage.getItem('token');
    const res = await axios.get(`/api/technician-inventory/bundle-print/${warehouseId}`, {
      headers: {
        'x-auth-token': token
      }
    });
    return res.data;
  } catch (error) {
    throw error.response?.data || { message: 'Error fetching parts for bundle printing' };
  }
};

// Create bundle labels
export const createBundleLabels = async (warehouseId) => {
  try {
    const token = localStorage.getItem('token');
    const res = await axios.post(`/api/technician-inventory/create-bundles/${warehouseId}`, {}, {
      headers: {
        'x-auth-token': token
      }
    });
    return res.data;
  } catch (error) {
    throw error.response?.data || { message: 'Error creating bundle labels' };
  }
};

// Get QR code status without updating
export const getQRCodeStatus = async (qrCode) => {
  try {
    const token = localStorage.getItem('token');
    // URL encode the QR code to handle special characters like slashes
    const encodedQrCode = encodeURIComponent(qrCode);
    const res = await axios.get(`/api/technician-inventory/qr-status/${encodedQrCode}`, {
      headers: {
        'x-auth-token': token
      }
    });
    return res.data;
  } catch (error) {
    throw error.response?.data || { message: 'Error fetching QR code status' };
  }
};

// Update bundle status via QR code scan
export const updateBundleStatusByQR = async (qrCode, newStatus) => {
  try {
    const token = localStorage.getItem('token');
    const res = await axios.post('/api/technician-inventory/update-by-qr', {
      qr_code: qrCode,
      status: newStatus
    }, {
      headers: {
        'Content-Type': 'application/json',
        'x-auth-token': token
      }
    });
    return res.data;
  } catch (error) {
    throw error.response?.data || { message: 'Error updating bundle status' };
  }
};

// Helper function to format date for display
export const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};
